import { isEmpty, get, flowRight, toNumber, isNumber } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { focus } from 'redux-form';

import { POST_STATUS } from '@wix/communities-blog-universal/dist/src/constants/post';

import { EXPERIMENT_BM_PREVIEW_POST } from '@wix/communities-blog-experiments';
import { flushStartTyping } from '../../actions/typing-socket';
import { COMMENTS_PER_PAGE } from '@wix/communities-blog-client-common/dist/src/constants/pagination';
import { fetchComments } from '../../actions/fetch-comments';
import { fetchPost } from '../../actions/fetch-post';
import fetchRecentPosts from '../../actions/fetch-recent-posts';
import { incrementCommentLikeCount } from '../../actions/increment-comment-like-count';
import { incrementPostViewCount } from '../../actions/increment-post-view-count';
import { pageOpened } from '../../actions/page-opened';
import { emitOpenPost, emitClosePost } from '../../actions/post-socket';
import { setIsLoaded } from '../../actions/set-is-loaded';
import { setIsPostInPreview } from '../../actions/set-is-post-in-preview';
import { clearComments } from '../../actions/clear-comments';
import AnimatedLoader from '../../components/animated-loader';
import CommentingDisabled from '../../components/commenting-disabled';
import AppLoaded from '../../components/app-loaded';
import PageMetadata from '../../components/page-metadata';
import Pagination from '../../components/pagination-comments';
import PostPageLayout from '../../components/post-page-layout';
import Post from '../../components/post';
import PostCommentList from '../post-comment-list';
import RecentPosts from '../../components/recent-posts';
import JsonSchema from '../../components/json-schema';
import PostPageFooter from '../post-page-footer';
import { POST_PAGE, isInPostPreview } from '../../services/detect-route';
import { getPostCover } from '../../services/get-post-cover';
import hashLinkHandler from '../../services/hash-link-handler';
import { getLastPage } from '../../services/pagination';
import { push, replace } from '../../services/router-utils';
import { resolvePostSlug } from '../../services/slug';
import { getConfig } from '../../services/config';
import { getPostSchema } from '../../services/json-schema';
import { scrollToAppTop } from '../../services/scroll-to';
import { getPostSeoTitle, getPostSeoDescription } from '../../../post-settings/services/post-settings';
import { getCommentsByPostId } from '../../selectors/comment-selectors';
import {
  getCurrentUserId,
  getCurrentUser,
} from '@wix/communities-blog-client-common/dist/src/selectors/current-user-selectors';
import { getIsPostLoaded, getIsCommentsLoaded, getIsRecentPostsLoaded } from '../../selectors/is-loaded-selectors';
import { getEntityCount } from '../../selectors/pagination-selectors';
import { getPostBySlug, getPostByIdOrSlug, getPostCanonicalUrl, getPostAmpUrl } from '../../selectors/post-selectors';
import { getRecentPosts } from '../../selectors/recent-posts-selectors';
import { getCurrentLocationSearch } from '../../selectors/location-history-selectors';
import { getIsDemoMode } from '../../selectors/is-demo-mode-selectors';
import { isSeo, isEditor } from '../../selectors/view-mode-selectors';
import { isExperimentEnabled } from '@wix/communities-blog-client-common/dist/src/selectors/experiments-selectors';
import { getIsAppLoaded } from '../../selectors/is-app-loaded-selectors';
import { getPostPageEnabled } from '../../selectors/app-data-selectors';
import { getCategoriesMap } from '../../selectors/categories-selectors';
import { getIsRecentPostsEnabled } from '../../selectors/app-settings-selectors';
import withFeedMetadataSettings from '../../hoc/with-feed-metadata-settings';
import { isPostPageBundle } from '../../services/section-route-utils';
import withAuth from '../../hoc/with-auth';
import withCardBorderWidth from '../../hoc/with-card-border-width';
import withDeviceType from '../../hoc/with-device-type';
import withFontClassName from '../../hoc/with-font-class-name';
import withTranslate from '../../hoc/with-translate';
import styles from './post-page.scss';
import createPermissions from '../../services/create-permission-checker';
import { createPermissionsChecker } from '../../services/create-permission-helpers';
import { redirectTo404 } from '../../actions/redirect-to-404';

const canPreviewPost = (state, post) => {
  if (!get(state, 'auth.isAuthenticated')) {
    return false;
  }

  const user = getCurrentUser(state);
  const perm = createPermissions(state, user);
  const can = createPermissionsChecker(perm);
  return can('preview', 'post', post);
};

export class PostPage extends Component {
  state = { isFetchingPost: false };

  static fetchData = ({ store, params, pathname, search }) => {
    const postSlug = resolvePostSlug(params);
    return store
      .dispatch(fetchPost(postSlug))
      .then(post => {
        const state = store.getState();

        if (isInPostPreview(pathname)) {
          return store.dispatch(canPreviewPost(state, post) ? setIsPostInPreview(true) : redirectTo404());
        }

        if (post.status !== POST_STATUS.published) {
          return store.dispatch(push({ pathname: `/${post._id}/edit${search}` }));
        }

        if (isSeo(state) && getIsRecentPostsEnabled(state)) {
          return store.dispatch(fetchRecentPosts(post));
        }
      })
      .catch(error => {
        if (error.status === 401) {
          return store.dispatch(
            push({
              pathname: `/login${search}&redirect=/${postSlug}`,
            }),
          );
        }

        throw error;
      });
  };

  fetchPost = () => {
    this.setState({ isFetchingPost: true });
    const { params, push, page } = this.props;
    const { categorySlug } = params;

    this.props
      .fetchPost(resolvePostSlug(params))
      .then(post => this.onPostLoaded({ post, page }))
      .catch(error => {
        if (error.status === 401) {
          push(categorySlug ? `/${categorySlug}` : '/');
        }
      });
  };

  emitOpenPost = post => post._id && this.props.emitOpenPost(post);

  emitClosePost = post => {
    if (post._id) {
      this.props.flushStartTyping();
      this.props.emitClosePost(post);
    }
  };

  componentDidMount = () => {
    const { page, post, isAppLoaded } = this.props;

    this.emitOpenPost(post);

    if (!isAppLoaded) {
      this.onPostLoaded({ post, page });
    } else {
      this.fetchPost();
    }
  };

  onPostLoaded = ({ post, page }) => {
    const {
      incrementPostViewCount,
      fetchRecentPosts,
      clearComments,
      pageOpened,
      shouldIncrementViewCount,
      showRecentPosts,
      showComments,
      isMobile,
      isEditor,
    } = this.props;

    this.setState({ isFetchingPost: false });
    shouldIncrementViewCount && incrementPostViewCount(post._id);

    if (showComments) {
      clearComments();
      this.fetchComments(post, page);
    }
    showRecentPosts && fetchRecentPosts(post);

    pageOpened({
      page: POST_PAGE,
      post,
      isMobile,
      isEditor,
    });
  };

  fetchComments = (post, page) =>
    this.props.fetchComments({
      postId: post._id,
      pageSize: COMMENTS_PER_PAGE,
      page,
    });

  onPostClosed = post => {
    this.props.setIsLoaded('comments', post.slug, false);
  };

  fetchCommentsAndRecentPosts = () => {
    const { post, page, clearComments, fetchRecentPosts, showRecentPosts, showComments } = this.props;

    showRecentPosts && fetchRecentPosts(post);
    if (showComments) {
      clearComments();
      this.fetchComments(post, page);
    }
  };

  componentDidUpdate = ({
    post: prevPost,
    postSlug: prevPostSlug,
    showRecentPosts: prevShowRecentPosts,
    showComments: prevShowComments,
  }) => {
    hashLinkHandler(this.props.location);
    const { post, postSlug, page, fetchRecentPosts, showRecentPosts, showComments } = this.props;

    if (showRecentPosts && showRecentPosts !== prevShowRecentPosts) {
      fetchRecentPosts(post);
    }

    if (showComments && showComments !== prevShowComments) {
      clearComments();
      this.fetchComments(post, page);
    }

    if (post._id !== prevPost._id) {
      this.emitClosePost(prevPost);
      this.emitOpenPost(post);
    }

    if (!postSlug || prevPostSlug === postSlug) {
      return;
    }

    if (!isEmpty(post)) {
      this.fetchCommentsAndRecentPosts();
    }

    if (isEmpty(post) && !this.state.isFetchingPost) {
      this.onPostClosed(prevPost);
      this.fetchPost();
    }
  };

  componentWillUnmount = () => {
    this.emitClosePost(this.props.post);
  };

  setFirstCommentBottomPosition = firstCommentBottomPosition => {
    this.setState({ firstCommentBottomPosition });
  };

  renderPagination = () => {
    const { page, entityCount, isMobile, isCommentsLoaded } = this.props;
    if (!isMobile && isCommentsLoaded && isNumber(entityCount)) {
      return (
        <Pagination
          page={page}
          entityCount={entityCount}
          pageSize={COMMENTS_PER_PAGE}
          onChange={this.handlePageChange}
        />
      );
    }
  };

  getPathName = () => {
    const postSlug = resolvePostSlug(this.props.params);
    const { categorySlug } = this.props.params;
    return categorySlug ? `/${categorySlug}/${postSlug}` : `/${postSlug}`;
  };

  handleCommentCreate = () => {
    const { post, entityCount, push, page } = this.props;
    const lastPage = getLastPage(entityCount, COMMENTS_PER_PAGE);
    if (page === lastPage) {
      return;
    }

    this.fetchComments(post, lastPage);
    push({
      pathname: this.getPathName(),
      query: { page: lastPage },
    });
  };

  handlePageChange = ({ page }) => {
    const { post } = this.props;
    this.fetchComments(post, page).then(scrollToAppTop);
    this.props.push({ pathname: this.getPathName(), query: { page } });
  };

  loadMore = page => this.fetchComments(this.props.post, page);

  renderCommentList = () => (
    <PostCommentList
      loadMore={this.loadMore}
      page={this.props.page}
      setFirstCommentBottomPosition={this.setFirstCommentBottomPosition}
      postSlug={resolvePostSlug(this.props.params)}
    />
  );

  renderCommentsWithForm = () =>
    this.props.isMobile ? (
      <div>
        {this.renderCommentList()}
        {this.renderPagination()}
        {this.renderFooter()}
      </div>
    ) : (
      <div className={classNames(styles.comments, !this.props.isPostPageBundle && styles.notPostPageApp)}>
        {this.renderFooter()}
        {this.renderCommentList()}
        {this.renderPagination()}
      </div>
    );

  renderFooter = () => {
    const { isBlocked, post, t, allComments } = this.props;
    if (isBlocked || post.isCommentsDisabled) {
      return (
        <CommentingDisabled message={t(isBlocked ? 'post-page.commenting-blocked' : 'post-page.commenting-disabled')} />
      );
    }

    return (
      <PostPageFooter
        onCommentCreate={this.handleCommentCreate}
        postId={post._id}
        isSticky={Boolean(this.state.firstCommentBottomPosition && allComments.length)}
        stickyThreshold={this.state.firstCommentBottomPosition}
      />
    );
  };

  render = () => {
    const {
      post,
      recentPosts,
      contentFontClassName,
      isPostLoaded,
      isCommentsLoaded,
      isRecentPostsLoaded,
      page,
      location,
      isSeo,
      canonicalUrl,
      ampUrl,
      jsonSchema,
      showRecentPosts,
      showComments,
    } = this.props;

    const { imageMetadata } = getPostCover(post);

    return (
      <PostPageLayout className={classNames(styles.postPage, contentFontClassName)} data-hook="post-page">
        {ampUrl ? <link rel="amphtml" href={ampUrl} /> : null}
        <PageMetadata
          title={getPostSeoTitle(post)}
          description={getPostSeoDescription(post)}
          canonicalUrl={canonicalUrl}
          ampUrl={ampUrl}
          image={imageMetadata}
          type="article"
        />
        {jsonSchema && <JsonSchema content={jsonSchema} />}
        <AnimatedLoader isLoading={!isPostLoaded && isEmpty(post)}>
          {page > 1 ? null : (
            <div className={styles.post}>
              <Post key={post._id} post={post} location={location} isInPostPage />
              <AppLoaded key={post.slug} />
            </div>
          )}
          {showRecentPosts && (
            <AnimatedLoader isLoading={!isRecentPostsLoaded}>
              <div className={styles.recentPosts}>
                <RecentPosts posts={recentPosts} />
              </div>
            </AnimatedLoader>
          )}
          {!isSeo && showComments && (
            <AnimatedLoader isLoading={!isCommentsLoaded} isAnimated>
              {this.renderCommentsWithForm()}
            </AnimatedLoader>
          )}
        </AnimatedLoader>
      </PostPageLayout>
    );
  };
}

PostPage.propTypes = {
  postSlug: PropTypes.string,
  fetchPost: PropTypes.func.isRequired,
  fetchComments: PropTypes.func.isRequired,
  fetchRecentPosts: PropTypes.func.isRequired,
  incrementPostViewCount: PropTypes.func.isRequired,
  incrementCommentLikeCount: PropTypes.func.isRequired,
  clearComments: PropTypes.func.isRequired,
  post: PropTypes.object.isRequired,
  recentPosts: PropTypes.array,
  allComments: PropTypes.array.isRequired,
  focusCommentForm: PropTypes.func.isRequired,
  location: PropTypes.object,
  page: PropTypes.number,
  isMobile: PropTypes.bool,
  emitOpenPost: PropTypes.func.isRequired,
  emitClosePost: PropTypes.func.isRequired,
  pageOpened: PropTypes.func.isRequired,
  t: PropTypes.func,
  typing: PropTypes.array,
  ownerId: PropTypes.string,
  entityCount: PropTypes.number,
  isAuthenticated: PropTypes.bool,
  isBlocked: PropTypes.bool,
  params: PropTypes.object,
  borderWidth: PropTypes.number.isRequired,
  titleFontClassName: PropTypes.string.isRequired,
  contentFontClassName: PropTypes.string.isRequired,
  flushStartTyping: PropTypes.func.isRequired,
  setIsLoaded: PropTypes.func.isRequired,
  isPostLoaded: PropTypes.bool.isRequired,
  isCommentsLoaded: PropTypes.bool.isRequired,
  isRecentPostsLoaded: PropTypes.bool.isRequired,
  shouldIncrementViewCount: PropTypes.bool.isRequired,
  push: PropTypes.func.isRequired,
  replace: PropTypes.func.isRequired,
  search: PropTypes.string.isRequired,
  isSeo: PropTypes.bool.isRequired,
  isAppLoaded: PropTypes.bool.isRequired,
  canonicalUrl: PropTypes.string,
  ampUrl: PropTypes.string,
  jsonSchema: PropTypes.object,
  showRecentPosts: PropTypes.bool,
  showComments: PropTypes.bool,
  isPostPageBundle: PropTypes.bool.isRequired,
  isEditor: PropTypes.bool,
};

PostPage.defaultProps = {
  shouldIncrementViewCount: true,
};

const mapStateToProps = (state, ownProps) => {
  const postSlug = resolvePostSlug(ownProps.params);
  const isPreviewPostEnabled = isExperimentEnabled(state, EXPERIMENT_BM_PREVIEW_POST);
  const useDraft = isInPostPreview(get(ownProps, 'location.pathname', ''));
  let post = (isPreviewPostEnabled ? getPostByIdOrSlug(state, postSlug) : getPostBySlug(state, postSlug)) || {};
  post = isPreviewPostEnabled && useDraft ? { ...post, ...(post.draft || {}) } : post;
  const postId = post._id;
  const page = toNumber(get(ownProps, 'location.query.page', 1));
  const { sectionUrl: blogSectionUrl, postPageSectionUrl, publisher } = getConfig();
  const isPostPageEnabled = getPostPageEnabled(state);

  return {
    postSlug,
    post,
    allComments: getCommentsByPostId(state, postId),
    recentPosts: getRecentPosts(state, postId),
    typing: state.typing,
    ownerId: getCurrentUserId(state),
    isPostLoaded: getIsPostLoaded(state, postSlug),
    isCommentsLoaded: getIsCommentsLoaded(state, postId),
    isRecentPostsLoaded: getIsRecentPostsLoaded(state, postId),
    shouldIncrementViewCount: !(getIsDemoMode(state) || useDraft),
    entityCount: getEntityCount(state, 'comments'),
    page,
    search: getCurrentLocationSearch(state),
    isSeo: isSeo(state),
    isAppLoaded: getIsAppLoaded(state),
    canonicalUrl: getPostCanonicalUrl(state, post),
    ampUrl: getPostAmpUrl(state, postId),
    jsonSchema: getPostSchema(
      post,
      getCategoriesMap(state),
      blogSectionUrl,
      postPageSectionUrl,
      isPostPageEnabled,
      publisher,
    ),
    isPostPageBundle: isPostPageBundle(),
    isEditor: isEditor(state),
  };
};

const mapDispatchToProps = {
  fetchComments,
  fetchRecentPosts,
  fetchPost,
  incrementPostViewCount,
  incrementCommentLikeCount,
  focusCommentForm: () => focus('comment', 'content'),
  emitOpenPost,
  emitClosePost,
  flushStartTyping,
  pageOpened,
  setIsLoaded,
  push,
  replace,
  clearComments,
};

export default flowRight(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withFeedMetadataSettings,
  withDeviceType,
  withFontClassName,
  withCardBorderWidth,
  withAuth,
  withTranslate,
)(PostPage);
